import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { motion } from 'framer-motion';
import Navbar from './components/Navbar';
import UniverseBackground from './components/UniverseBackground';
import Hero from './components/Hero';
import AboutSection from './components/AboutSection';
import BeyondTheWalls from './components/BeyondTheWalls';
import AICourseLaunch from './components/AICourseLaunch';
import BlogSection from './components/BlogSection';
import BlogPage from './components/BlogPage';
import ContactUs from './components/ContactUs';
import CertificationPage from './components/CertificationPage';
import PythonCourse from './components/PythonCourse';
import AIForEveryoneCourse, { GenAIEnrollPage } from './components/AIForEveryoneCourse';
import AIDataCoursePage from './components/AIDataCoursePage';
import BusinessAnalystCareerAccelerator from './components/BusinessAnalystCareerAccelerator';
import RoboticsCourse from './components/RoboticsCourse';
import DataScienceCourse from './components/DataScienceCourse';
import AttendanceGenerator from './components/AttendanceGenerator';
import BeyondGalleryPage from './components/BeyondGalleryPage';
import CommunityClubPage from './components/CommunityClubPage';
import LiveEventsPage from './components/LiveEventsPage';
import InternshipHubPage from './components/InternshipHubPage';
import InternshipDetailPage from './components/InternshipDetailPage';
import AITrainingDashboard from './components/AITrainingDashboard';
import InterviewPrepPythonPage from './components/InterviewPrepPythonPage';
import OurCreationsPage from './components/OurCreationsPage';
import JobReadyProgramPage from './components/JobReadyProgramPage';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Couchangesrsera-style learning platform theme
const PRIMARY = '#2563EB';
const PRIMARY_HOVER = '#1D4ED8';
const BG = '#F8FAFC';
const CARD_BG = '#FFFFFF';
const TEXT_DARK = '#1E293B';
const BORDER = '#E5E7EB';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: PRIMARY, light: '#93C5FD', dark: PRIMARY_HOVER },
    secondary: { main: TEXT_DARK, light: '#64748B', dark: '#0F172A' },
    success: { main: '#10b981', light: '#34d399', dark: '#059669' },
    warning: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706' },
    error: { main: '#ef4444', light: '#f87171', dark: '#dc2626' },
    info: { main: '#06b6d4', light: '#22d3ee', dark: '#0891b2' },
    background: { default: BG, paper: CARD_BG },
    text: { primary: TEXT_DARK, secondary: '#64748B' },
    divider: BORDER,
  },
  typography: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    h1: { fontWeight: 700, fontSize: '3.5rem', color: TEXT_DARK },
    h2: { fontWeight: 700, fontSize: '2.125rem', color: TEXT_DARK },
    h3: { fontWeight: 600, fontSize: '2rem', color: TEXT_DARK },
    h4: { fontWeight: 600, color: TEXT_DARK },
    h5: { fontWeight: 600, color: TEXT_DARK },
    h6: { fontWeight: 600, color: TEXT_DARK },
    body1: { color: '#64748B', lineHeight: 1.7, fontSize: '1.0625rem' },
    body2: { color: '#64748B', lineHeight: 1.6 },
  },
  shape: { borderRadius: 18 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 30,
          padding: '12px 24px',
          transition: '0.3s',
        },
        contained: {
          boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
          '&:hover': {
            boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.06)',
          borderRadius: 18,
          border: `1px solid ${BORDER}`,
          background: CARD_BG,
          transition: '0.3s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          background: `${CARD_BG} !important`,
          borderBottom: `1px solid ${BORDER}`,
        },
      },
    },
  },
});

const TechnologiesWeUse = lazy(() => import('./components/TechnologiesWeUse'));
const StudentReview = lazy(() => import('./components/StudentReview'));
const WhatOurStudentsSay = lazy(() => import('./components/WhatOurStudentsSay'));

function AppContent() {
  const location = useLocation();
  const isBlogPage = location.pathname.startsWith('/blog');
  const isHomePage = location.pathname === '/';

  return (
    <>
      {!isBlogPage && isHomePage && <UniverseBackground />}
            <Navbar />
            <Routes>
              <Route path="/" element={
                <>
                  <Hero />
                  <AboutSection />
                  <BeyondTheWalls />
                  <AICourseLaunch />
                  <Suspense fallback={null}>
                    <TechnologiesWeUse />
                    <StudentReview />
                  </Suspense>
                  <BlogSection />
                  <Suspense fallback={null}>
                    <WhatOurStudentsSay />
                  </Suspense>
                </>
              } />
              <Route path="/certification" element={<CertificationPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPage />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/python-course" element={<PythonCourse />} />
              <Route path="/gen-ai-course" element={<AIForEveryoneCourse />} />
              <Route path="/gen-ai-course/enroll" element={<GenAIEnrollPage />} />
              <Route path="/gen-ai-course/business-analyst" element={<BusinessAnalystCareerAccelerator />} />
              <Route path="/gen-ai-course/ai-data-course" element={<AIDataCoursePage />} />
              <Route path="/gen-ai-course/attendance" element={<AttendanceGenerator />} />
              <Route path="/robotics-course" element={<RoboticsCourse />} />
              <Route path="/data-science-course" element={<DataScienceCourse />} />
              <Route path="/beyond-gallery" element={<BeyondGalleryPage />} />
              <Route path="/live-events" element={<LiveEventsPage />} />
              <Route path="/community-club" element={<CommunityClubPage />} />
              <Route path="/internship-hub" element={<InternshipHubPage />} />
              <Route path="/internship-hub/:id" element={<InternshipDetailPage />} />
              <Route path="/ai-training-dashboard" element={<AITrainingDashboard />} />
              <Route path="/interview-preparation/python" element={<InterviewPrepPythonPage />} />
              <Route path="/our-creations" element={<OurCreationsPage />} />
              <Route path="/job-ready-program" element={<JobReadyProgramPage />} />
            </Routes>
            <Footer />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <motion.div
            initial={false}
            animate={{ opacity: 1 }}
            transition={{ duration: 0 }}
            className="App"
            style={{ position: 'relative', zIndex: 0 }}
          >
            <Routes>
              <Route path="*" element={<AppContent />} />
            </Routes>
          </motion.div>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
